import React from 'react';

interface PageLoaderProps {
  /** Accessible label for screen readers (not shown visually by default). */
  label?: string;
  /** Optional short message shown below the spinner. */
  message?: string;
  /** Minimum height so layout doesn't jump. */
  minHeight?: string;
  /** 'default' = centered spinner; 'route' = top progress bar + compact spinner (for navigation). */
  variant?: 'default' | 'route';
}

const PageLoader: React.FC<PageLoaderProps> = ({
  label = 'Loading',
  message,
  minHeight = '60vh',
  variant = 'default',
}) => {
  const spinner = (
    <div className="relative flex items-center justify-center" aria-hidden>
      <div
        className="animate-spin rounded-full border-2 border-stone-200 border-t-stone-800"
        style={{ width: variant === 'route' ? 32 : 48, height: variant === 'route' ? 32 : 48 }}
      />
    </div>
  );

  if (variant === 'route') {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4 px-6 bg-stone-50"
        style={{ minHeight }}
        role="status"
        aria-label={label}
        aria-live="polite"
      >
        {/* Indeterminate progress bar at top */}
        <div
          className="fixed top-0 left-0 right-0 h-0.5 bg-stone-200 overflow-hidden z-[100]"
          aria-hidden
        >
          <div className="page-loader-bar h-full w-1/3 bg-stone-800" />
        </div>
        <style dangerouslySetInnerHTML={{ __html: `
          .page-loader-bar { animation: page-loader-bar 1.2s ease-in-out infinite; }
          @keyframes page-loader-bar {
            0% { transform: translateX(-100%); }
            50% { transform: translateX(300%); }
            100% { transform: translateX(-100%); }
          }
          @media (prefers-reduced-motion: reduce) {
            .page-loader-bar { animation: none; opacity: 0.6; }
          }
        `}} />
        {spinner}
        <span className="sr-only">{label}</span>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col items-center justify-center gap-6 px-6"
      style={{ minHeight }}
      role="status"
      aria-label={label}
      aria-live="polite"
    >
      {spinner}
      {message && (
        <p className="text-sm font-medium text-stone-500" aria-hidden>
          {message}
        </p>
      )}
      <span className="sr-only">{label}</span>
    </div>
  );
};

export default PageLoader;
